import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import heroVideo from "../assets/dgb-hero.mp4";

/* ─────────────────────────────────────────────
   404 — NOT FOUND
   Matches the site's dark-teal design language.
   Redirects to / after a 4-second countdown.
───────────────────────────────────────────── */
export default function NotFound() {
  const navigate = useNavigate();
  const [count, setCount] = useState(4);

  useEffect(() => {
    if (count <= 0) {
      navigate("/", { replace: true });
      return;
    }
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count, navigate]);

  return (
    <div
      style={{
        minHeight: "100svh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0a1210",
      }}
    >
      {/* ── Video background ── */}
      <video
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          opacity: 0.22,
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <source src={heroVideo} type="video/mp4" />
      </video>

      {/* ── Gradient overlays ── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse at 60% 40%, rgba(54,196,168,0.07), transparent 55%), linear-gradient(180deg, rgba(10,18,16,0.6) 0%, rgba(10,18,16,0.88) 100%)",
        }}
      />

      {/* ── Blueprint corners ── */}
      {[
        { style: { top: 32, left: 32 }, rotate: "0deg" },
        { style: { top: 32, right: 32 }, rotate: "90deg" },
        { style: { bottom: 32, left: 32 }, rotate: "270deg" },
        { style: { bottom: 32, right: 32 }, rotate: "180deg" },
      ].map((corner, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            position: "absolute",
            ...corner.style,
            zIndex: 3,
            opacity: 0.5,
          }}
        >
          <svg width="32" height="32" viewBox="0 0 32 32">
            <g transform={`rotate(${corner.rotate} 16 16)`}>
              <line x1="0" y1="0" x2="20" y2="0" stroke="#248277" strokeWidth="0.8" />
              <line x1="0" y1="0" x2="0" y2="20" stroke="#248277" strokeWidth="0.8" />
              <circle cx="5.5" cy="5.5" r="2.5" fill="none" stroke="#248277" strokeWidth="0.5" />
            </g>
          </svg>
        </div>
      ))}

      {/* ── Schematic grid deco ── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "10%",
          right: "6%",
          width: "260px",
          height: "260px",
          zIndex: 2,
          opacity: 0.07,
          pointerEvents: "none",
        }}
      >
        <svg width="260" height="260" viewBox="0 0 260 260">
          {[0, 1, 2, 3].map((i) => (
            <line key={`h${i}`} x1="0" y1={i * 86} x2="260" y2={i * 86} stroke="#36c4a8" strokeWidth="0.5" strokeDasharray="4 7" />
          ))}
          {[0, 1, 2, 3].map((i) => (
            <line key={`v${i}`} x1={i * 86} y1="0" x2={i * 86} y2="260" stroke="#36c4a8" strokeWidth="0.5" strokeDasharray="4 7" />
          ))}
          <circle cx="130" cy="130" r="84" fill="none" stroke="#36c4a8" strokeWidth="0.5" strokeDasharray="3 5" />
          <circle cx="130" cy="130" r="50" fill="none" stroke="#36c4a8" strokeWidth="0.4" />
          <circle cx="130" cy="130" r="3" fill="#36c4a8" />
        </svg>
      </div>

      {/* ── Scan line ── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: "linear-gradient(90deg, transparent, rgba(54,196,168,0.3), transparent)",
          animation: "scanLine 6s linear infinite",
          zIndex: 3,
          pointerEvents: "none",
        }}
      />

      {/* ── Top telemetry ── */}
      <div
        style={{
          position: "absolute",
          top: 40,
          left: 56,
          zIndex: 4,
          fontFamily: "var(--font-mono)",
          fontSize: "9px",
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: "#248277",
          lineHeight: 1.8,
        }}
      >
        <div>N 40°43′ &nbsp; W 74°00′</div>
        <div style={{ opacity: 0.6 }}>◌ &nbsp; ATELIER 01 &nbsp; ◌ &nbsp; DIGITAL BILLIONS</div>
      </div>
      <div
        style={{
          position: "absolute",
          top: 40,
          right: 56,
          zIndex: 4,
          textAlign: "right",
          fontFamily: "var(--font-mono)",
          fontSize: "9px",
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: "#248277",
          lineHeight: 1.8,
        }}
      >
        <div>SYS:ERR &nbsp; ◌ &nbsp; CODE 404</div>
        <div style={{ opacity: 0.6 }}>◌ &nbsp; ROUTE NOT FOUND</div>
      </div>

      {/* ── Main content ── */}
      <div
        style={{
          position: "relative",
          zIndex: 4,
          textAlign: "center",
          padding: "0 24px",
          animation: "fadeUp 0.7s cubic-bezier(0.16,1,0.3,1) both",
        }}
      >
        {/* Label */}
        <p
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "10px",
            letterSpacing: "0.35em",
            textTransform: "uppercase",
            color: "#248277",
            marginBottom: 24,
          }}
        >
          ◈ &nbsp; DIGITAL BILLIONS GROUP &nbsp; ◈ &nbsp; ERROR 404
        </p>

        {/* Ghost 404 */}
        <div
          aria-hidden="true"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 300,
            fontSize: "clamp(80px, 18vw, 200px)",
            lineHeight: 0.85,
            letterSpacing: "-0.04em",
            color: "rgba(240,250,247,0.05)",
            userSelect: "none",
            pointerEvents: "none",
            marginBottom: 0,
          }}
        >
          404
        </div>

        {/* Headline */}
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 300,
            fontSize: "clamp(28px, 4.5vw, 60px)",
            lineHeight: 1.0,
            letterSpacing: "-0.02em",
            color: "#f0faf7",
            marginTop: -12,
            marginBottom: 20,
          }}
        >
          Signal Lost.
          <br />
          <em
            style={{
              color: "#36c4a8",
              textShadow: "0 0 16px rgba(54,196,168,0.55)",
            }}
          >
            Route Not Found.
          </em>
        </h1>

        <p
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 300,
            fontSize: "16px",
            lineHeight: 1.8,
            color: "rgba(240,250,247,0.55)",
            maxWidth: "400px",
            margin: "0 auto 44px",
          }}
        >
          The page you're looking for doesn't exist or has been moved.
          You'll be returned to base shortly.
        </p>

        {/* Countdown ring */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
            marginBottom: 36,
          }}
        >
          <div style={{ position: "relative", width: 72, height: 72 }}>
            <svg
              width="72"
              height="72"
              viewBox="0 0 72 72"
              style={{ position: "absolute", inset: 0 }}
            >
              <circle
                cx="36"
                cy="36"
                r="30"
                fill="none"
                stroke="rgba(53,143,128,0.15)"
                strokeWidth="1.5"
              />
              <circle
                cx="36"
                cy="36"
                r="30"
                fill="none"
                stroke="#36c4a8"
                strokeWidth="1.5"
                strokeDasharray={`${(count / 4) * 188.5} 188.5`}
                strokeLinecap="round"
                transform="rotate(-90 36 36)"
                style={{ transition: "stroke-dasharray 0.95s linear" }}
              />
            </svg>
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--font-display)",
                fontWeight: 300,
                fontSize: "28px",
                lineHeight: 1,
                color: "#36c4a8",
                textShadow: "0 0 12px rgba(54,196,168,0.5)",
              }}
            >
              {count}
            </div>
          </div>
          <p
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "9px",
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              color: "#248277",
            }}
          >
            Returning to base…
          </p>
        </div>

        {/* Manual CTA */}
        <a
          href="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "13px 28px",
            fontFamily: "var(--font-mono)",
            fontSize: "10px",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "#f0faf7",
            border: "1px solid rgba(53,143,128,0.35)",
            textDecoration: "none",
            transition: "all 0.35s cubic-bezier(0.16,1,0.3,1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(54,196,168,0.08)";
            e.currentTarget.style.borderColor = "rgba(102,213,197,0.5)";
            e.currentTarget.style.color = "#36c4a8";
            e.currentTarget.style.letterSpacing = "0.24em";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "rgba(53,143,128,0.35)";
            e.currentTarget.style.color = "#f0faf7";
            e.currentTarget.style.letterSpacing = "0.2em";
          }}
        >
          <span>Return Home Now</span>
          <span aria-hidden="true">↗</span>
        </a>
      </div>

      {/* ── Bottom telemetry ── */}
      <div
        style={{
          position: "absolute",
          bottom: 28,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 4,
          fontFamily: "var(--font-mono)",
          fontSize: "9px",
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: "rgba(36,130,119,0.45)",
          whiteSpace: "nowrap",
        }}
      >
        DBG-HQ &nbsp; ◌ &nbsp; GLOBAL &nbsp; ◌ &nbsp; MMXXVI
      </div>
    </div>
  );
}
